// ---------------------------------------------------------------------------
// useTrendsPulseData -- Fetches all metrics in parallel for Pulse Board KPI cards
// ---------------------------------------------------------------------------
// Makes N parallel "group_by=none" calls (one per metric) for sparklines/totals,
// plus one grouped call for the focused metric's main chart.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { profilesService } from '@/services/v11/profilesService';
import { contentService } from '@/services/v11/contentService';
import { commentsService } from '@/services/v11/commentsService';
import { brandsService } from '@/services/v11/brandsService';
import { teamsService } from '@/services/v11/teamsService';
import type { DataSourceId } from '@/components/v11/discovery/types';
import type { VanityMetric, TimelineDataPoint } from '@/components/v11/discovery/TrendsPanel';
import type { MetricSummary } from '@/components/v11/discovery/TrendsPulseBoard';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const METRICS_BY_SOURCE: Record<string, VanityMetric[]> = {
  profiles: ['new_profiles', 'comments'],
  content: ['likes', 'views', 'comments', 'engagement_rate', 'amplification'],
  comments: ['comments', 'comment_likes', 'replies', 'sentiment'],
  brands: ['total_detections', 'unique_profiles', 'audience_comments', 'audience_likes'],
  hashtags: ['posts_count', 'engagement_rate'],
  teams: ['total_mentions', 'profile_mentions', 'post_mentions', 'comment_mentions'],
  channels: ['posts', 'likes', 'comments', 'interactions'],
};

// ---------------------------------------------------------------------------
// Service dispatcher
// ---------------------------------------------------------------------------

function getTrendsForSource(
  dataSource: DataSourceId,
  metric: string,
  groupBy: string,
  filters: Record<string, unknown>
): Promise<unknown> {
  switch (dataSource) {
    case 'profiles':
      return profilesService.getTrends(metric, groupBy, filters as any);
    case 'content':
      return contentService.getTrends(metric, groupBy, filters as any);
    case 'comments':
      return commentsService.getTrends(metric, groupBy, filters as any);
    case 'brands':
      return brandsService.getTrends(metric, groupBy, filters as any);
    case 'teams':
      return teamsService.getTrends(metric, groupBy, filters as any);
    default:
      return Promise.resolve(null);
  }
}

// ---------------------------------------------------------------------------
// Timeline helpers
// ---------------------------------------------------------------------------

function extractTimeline(apiData: any): TimelineDataPoint[] {
  if (!apiData?.data?.timeline) return [];
  return apiData.data.timeline.map((point: any) => ({
    period: point.periodLabel || point.period,
    fullPeriod: point.period,
    periodLabel: point.periodLabel || point.period,
    ...point,
  }));
}

/**
 * Convert a "none" grouped timeline into a MetricSummary.
 * The timeline has { period, value } entries — we compute total, previous, sparkline.
 */
function timelineToSummary(metric: VanityMetric, timeline: any[]): MetricSummary {
  if (!timeline || timeline.length === 0) {
    return { metric, currentValue: 0, previousValue: 0, recentHalfValue: 0, sparkline: [] };
  }

  const values = timeline.map((p: any) => Number(p.value) || 0);

  // Split into first half / second half for period-over-period comparison
  const midpoint = Math.floor(values.length / 2);
  const previousHalf = values.slice(0, midpoint);
  const currentHalf = values.slice(midpoint);

  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);
  const avg = (arr: number[]) => (arr.length > 0 ? sum(arr) / arr.length : 0);

  // For rate metrics (engagement_rate, sentiment), use average instead of sum
  const isRateMetric = metric === 'engagement_rate' || metric === 'sentiment';
  const aggregate = isRateMetric ? avg : sum;

  return {
    metric,
    currentValue: aggregate(values),
    previousValue: aggregate(previousHalf),
    recentHalfValue: aggregate(currentHalf),
    sparkline: values,
  };
}

// ---------------------------------------------------------------------------
// Hook Options
// ---------------------------------------------------------------------------

export interface UseTrendsPulseDataOptions {
  readonly dataSource: DataSourceId;
  readonly apiFilters: Record<string, unknown>;
  readonly focusedMetric: VanityMetric;
  readonly groupBy: string;
  readonly isOpen: boolean;
  readonly granularity: string;
}

export interface UseTrendsPulseDataResult {
  /** KPI card summaries for all metrics */
  readonly summaries: readonly MetricSummary[];
  readonly isLoadingSummaries: boolean;
  /** Focused metric grouped timeline data (for main chart) */
  readonly focusedData: readonly TimelineDataPoint[];
  readonly isLoadingFocused: boolean;
  readonly focusedError: Error | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTrendsPulseData(options: UseTrendsPulseDataOptions): UseTrendsPulseDataResult {
  const { dataSource, apiFilters, focusedMetric, groupBy, isOpen, granularity } = options;

  const metrics = METRICS_BY_SOURCE[dataSource] || [];
  const filtersWithGranularity = useMemo(
    () => ({ ...apiFilters, granularity }),
    [apiFilters, granularity]
  );

  // -------------------------------------------------------------------------
  // Parallel queries: one per metric with group_by=none (for KPI summaries)
  // -------------------------------------------------------------------------
  const summaryQueries = useQueries({
    queries: metrics.map(metric => ({
      queryKey: ['v11-trends-summary', dataSource, metric, filtersWithGranularity],
      queryFn: () => getTrendsForSource(dataSource, metric, 'none', filtersWithGranularity),
      enabled: isOpen && dataSource !== 'hashtags',
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
    })),
  });

  const summaries: MetricSummary[] = useMemo(() => {
    return metrics.map((metric, i) => {
      const queryResult = summaryQueries[i];
      if (!queryResult?.data) {
        return { metric, currentValue: 0, previousValue: 0, recentHalfValue: 0, sparkline: [] };
      }
      const timeline = (queryResult.data as any)?.data?.timeline || [];
      return timelineToSummary(metric, timeline);
    });
  }, [metrics, summaryQueries]);

  const isLoadingSummaries = summaryQueries.some(q => q.isLoading);

  // -------------------------------------------------------------------------
  // Focused metric query (with group_by for the main chart)
  // -------------------------------------------------------------------------
  const {
    data: focusedApiData,
    isLoading: isLoadingFocused,
    error: focusedError,
  } = useQuery({
    queryKey: ['v11-trends-focused', dataSource, focusedMetric, groupBy, filtersWithGranularity],
    queryFn: () => getTrendsForSource(dataSource, focusedMetric, groupBy, filtersWithGranularity),
    enabled: isOpen && dataSource !== 'hashtags',
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });

  const focusedData = useMemo(() => extractTimeline(focusedApiData), [focusedApiData]);

  return {
    summaries,
    isLoadingSummaries,
    focusedData,
    isLoadingFocused,
    focusedError: focusedError as Error | null,
  };
}
